import React, { useEffect } from 'react';
import { 
  Scale, 
  Shield,
  Gavel,
  Globe,
  Target,
  HeartHandshake,
  Clock,
  Award,
  Users,
  MapPin,
  Phone,
  Star
} from 'lucide-react';
import LLSEOMeta from './LLSEOMeta';

const LLWhyUsPage = () => {
  // Schema markup for Local Business + Attorney
  useEffect(() => {
    const schemaMarkup = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "LegalService",
          "@id": "https://loopholelaw.in/#legalservice",
          "name": "LoopHoleLaw - Best Lawyers in Kollam, Kerala",
          "description": "Leading law firm in Kollam specializing in Criminal Defense, Civil Litigation, Cyber Law, Family Law, and Property Law. 7+ years experience, 100+ cases won, 92% success rate.",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "1st Floor, Ganga Building, Opposite Kollam Collectorate",
            "addressLocality": "Kollam",
            "addressRegion": "Kerala",
            "postalCode": "691013",
            "addressCountry": "IN"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "8.892817793613391",
            "longitude": "76.57290401478547"
          },
          "telephone": "+919539650421",
          "email": "loopholelawcontact@gmail.com",
          "url": "https://loopholelaw.in",
          "priceRange": "$$",
          "areaServed": [
            {
              "@type": "City",
              "name": "Kollam"
            },
            {
              "@type": "City",
              "name": "Karunagappally"
            },
            {
              "@type": "City",
              "name": "Sasthamcotta"
            },
            {
              "@type": "City",
              "name": "Kottarakkara"
            },
            {
              "@type": "City",
              "name": "Punalur"
            }
          ],
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "reviewCount": "250",
            "bestRating": "5",
            "worstRating": "1"
          },
          "knowsLanguage": ["English", "Malayalam", "Hindi"]
        },
        {
          "@type": "Attorney",
          "@id": "https://loopholelaw.in/#attorney",
          "name": "LoopHoleLaw",
          "description": "Best lawyers in Kollam with 7+ years experience in Kollam District Court and Kerala High Court. Specializing in Criminal, Civil, Cyber, Family, and Property Law.",
          "areaServed": {
            "@type": "State",
            "name": "Kerala"
          }
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Why is LoopHoleLaw considered the best law firm in Kollam?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "LoopHoleLaw has 7+ years of experience in Kollam District Court and Kerala High Court, with a proven track record of 100+ successful cases and 92% success rate in criminal defense. We provide multilingual service in English, Malayalam, and Hindi."
              }
            },
            {
              "@type": "Question",
              "name": "What areas in Kollam district do you serve?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We serve all areas in Kollam district including Kollam City, Karunagappally, Sasthamcotta, Kottarakkara, Punalur, Chavara, Paravur, Anchal, and Kundara."
              }
            },
            {
              "@type": "Question",
              "name": "What legal services does LoopHoleLaw provide in Kollam?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We specialize in Criminal Law, Civil Litigation, Cyber Law, Family Law, Property Law, Corporate Law, and Intellectual Property Law in Kollam, Kerala."
              }
            }
          ]
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaMarkup);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const expertise = [
    {
      icon: <Gavel className="w-12 h-12 text-green-400" />,
      title: "Criminal Law Excellence in Kollam",
      description: "Expert criminal defense in Kollam District Court with proven track record. Successfully handled 500+ criminal cases including bail applications, anticipatory bail, quashing petitions, and trial defense with 92% favorable outcomes.",
      stats: "500+ Cases | 92% Success Rate",
      area: "Criminal Law"
    },
    {
      icon: <Shield className="w-12 h-12 text-green-400" />,
      title: "Civil Litigation Success in Kerala",
      description: "Comprehensive civil litigation services across Kollam district. Expertise in property disputes, family law matters, contractual disputes, and partition suits. 95% success rate in civil proceedings at Kollam courts.",
      stats: "100+ Cases | 95% Success Rate",
      area: "Civil Law"
    },
    {
      icon: <Globe className="w-12 h-12 text-green-400" />,
      title: "Cyber Law Expertise",
      description: "Kerala's leading cyber law specialists. Expert defense in online fraud, cyberbullying, defamation cases, and IT Act violations. Successfully handled 100+ cyber crime cases in Kollam and across Kerala.",
      stats: "100+ Cyber Cases Resolved",
      area: "Cyber Law"
    }
  ];

  const achievements = [
    { 
      value: "7+", 
      label: "Years Serving Kollam",
      description: "Trusted legal experts in Kerala" 
    },
    { 
      value: "100+", 
      label: "Cases Successfully Won",
      description: "Across Kollam District Court & High Court" 
    },
    { 
      value: "92%", 
      label: "Criminal Defense Success Rate",
      description: "Best in Kollam, Kerala" 
    },
    { 
      value: "24/7", 
      label: "Emergency Legal Support",
      description: "Always available in Kollam" 
    }
  ];

  const clientBenefits = [
    {
      icon: <Target className="w-12 h-12 text-green-400" />,
      title: "Kollam District Court Expertise",
      description: "Deep knowledge of local court procedures and judges in Kollam. Our strategic approach, built on 7+ years of experience in Kollam District Court and Kerala High Court, ensures the best possible outcomes."
    },
    {
      icon: <HeartHandshake className="w-12 h-12 text-green-400" />,
      title: "Multilingual Client Service",
      description: "Fluent in English, Malayalam (മലയാളം), and Hindi. Round-the-clock access to your legal team with transparent communication throughout your case. Personalized attention in your preferred language."
    },
    {
      icon: <Clock className="w-12 h-12 text-green-400" />,
      title: "Rapid Response in Kollam",
      description: "Swift case resolution with efficient management. Emergency legal support available 24/7 across Kollam district. We understand time is crucial in legal matters, especially in criminal cases."
    }
  ];

  const testimonials = [
    {
      name: "Rajesh Kumar",
      location: "Kollam City",
      case: "Property Dispute",
      rating: 5,
      text: "Best property lawyers in Kollam! They resolved my land dispute case efficiently. Highly recommend for property matters in Kerala."
    },
    {
      name: "Priya Menon",
      location: "Karunagappally",
      case: "Family Law",
      rating: 5,
      text: "Excellent family law services. They handled my divorce case with sensitivity and professionalism. The best lawyers in Kollam for family matters."
    },
    {
      name: "Arun Nair",
      location: "Kottarakkara",
      case: "Criminal Defense",
      rating: 5,
      text: "Outstanding criminal defense lawyers! They got my bail approved quickly and eventually won the case. Forever grateful to LoopHoleLaw Kollam."
    }
  ];

  const serviceAreas = [
    "Kollam City", "Karunagappally", "Sasthamcotta", 
    "Kottarakkara", "Punalur", "Chavara", 
    "Paravur", "Anchal", "Kundara", "Pathanapuram",
    "Kottiyam", "Oachira", "Mynagappally"
  ];

  return (
    <>
      {/* SEO Meta Tags */}
      <LLSEOMeta 
        title="Best Lawyers in Kollam, Kerala | Criminal, Civil & Cyber Law Experts | LoopHoleLaw"
        description="Top-rated law firm in Kollam, Kerala with 7+ years experience. Expert lawyers in Criminal Defense, Civil Litigation, Cyber Law, Family & Property Law. 100+ cases won, 92% success rate. കൊല്ലത്തെ മികച്ച അഭിഭാഷകർ. Call: +91 9539650421"
        keywords="best lawyers in Kollam, top law firm Kollam, criminal lawyer Kollam, civil lawyer Kerala, cyber law expert Kollam, family lawyer Kollam, property lawyer Kollam, advocates Kollam, Kollam district court lawyers, Kerala lawyers, കൊല്ലം അഭിഭാഷകർ, legal services Kollam"
      />
      
      <article className="min-h-screen bg-gradient-to-b from-slate-900 to-black text-white">
        {/* Hero Section */}
        <section className="relative min-h-[60vh] flex items-center justify-center py-16 px-4" aria-label="hero">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-green-500/10" />
          <div className="relative z-10 text-center max-w-4xl mx-auto">
            <div className="relative w-24 h-24 mx-auto mb-8">
              <Scale className="w-24 h-24 text-blue-400 animate-pulse" />
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-green-400">
              Best Lawyers in Kollam, Kerala
            </h1>
            <p className="text-2xl md:text-3xl text-white mb-4">
              കൊല്ലത്തെ ഏറ്റവും മികച്ച അഭിഭാഷകർ
            </p>
            <p className="text-xl text-gray-300 mb-6">
              Expert Legal Defense in Criminal, Civil, Cyber, Family & Property Law
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-6 text-sm">
              <div className="flex items-center gap-2 bg-slate-800/50 px-4 py-2 rounded-full border border-green-400/30">
                <Award className="w-5 h-5 text-green-400" />
                <span>7+ Years in Kollam</span>
              </div>
              <div className="flex items-center gap-2 bg-slate-800/50 px-4 py-2 rounded-full border border-green-400/30">
                <Shield className="w-5 h-5 text-green-400" />
                <span>100+ Cases Won</span>
              </div>
              <div className="flex items-center gap-2 bg-slate-800/50 px-4 py-2 rounded-full border border-green-400/30">
                <Star className="w-5 h-5 text-green-400" />
                <span>92% Success Rate</span>
              </div>
            </div>
            <p className="text-lg text-gray-300">
              Serving Kollam District Court & Kerala High Court with Excellence
            </p>
          </div>
        </section>

        {/* Quick Stats Banner */}
        <section className="bg-slate-900 py-8 px-4 border-y border-slate-700">
          <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {achievements.map((item, index) => (
              <div key={index}>
                <div className="text-3xl md:text-4xl font-bold text-green-400 mb-1">
                  {item.value}
                </div>
                <div className="text-sm md:text-base text-white font-medium">{item.label}</div>
                <div className="text-xs text-gray-400">{item.description}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Why Choose Us - Key Reasons */}
        <section className="py-20 px-4 bg-gradient-to-b from-slate-900 to-black">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Why LoopHoleLaw is the Best Law Firm in Kollam
              </h2>
              <p className="text-xl text-gray-400">
                Trusted by 100+ clients across Kollam district | കൊല്ലം ജില്ലയിലെ വിശ്വസ്ത നിയമസേവനം
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {clientBenefits.map((benefit, index) => (
                <div 
                  key={index} 
                  className="p-8 bg-slate-800/50 rounded-xl border border-slate-700 hover:bg-slate-800/70 hover:border-green-400/50 transition-all"
                >
                  <div className="flex justify-center mb-6">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-center mb-4 text-green-400">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-300 text-center">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Expertise Section */}
        <section className="py-20 px-4 bg-slate-900" aria-label="expertise">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Specialized Legal Services in Kollam
              </h2>
              <p className="text-xl text-gray-400">
                Expert representation across multiple practice areas in Kerala
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {expertise.map((item, index) => (
                <div 
                  key={index} 
                  className="p-8 bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-xl border border-slate-700 hover:border-green-400/50 transition-all"
                >
                  <div className="flex justify-center mb-6">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-center mb-4 text-white">
                    {item.title}
                  </h3>
                  <p className="text-gray-300 mb-4">{item.description}</p>
                  <div className="text-center pt-4 border-t border-slate-700">
                    <span className="text-sm font-semibold text-green-400">{item.stats}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Practice Areas Links */}
            <div className="mt-12 text-center">
              <h3 className="text-xl font-semibold mb-6 text-white">
                Explore Our Practice Areas in Kollam
              </h3>
              <div className="flex flex-wrap justify-center gap-3">
                {[
                  { name: "Criminal Law", url: "/practice-areas/criminal-law" },
                  { name: "Civil Law", url: "/practice-areas/civil-law" },
                  { name: "Cyber Law", url: "/practice-areas/cyber-law" },
                  { name: "Family Law", url: "/practice-areas/family-law" },
                  { name: "Property Law", url: "/practice-areas/real-estate-law" },
                  { name: "Corporate Law", url: "/practice-areas/corporate-law" }
                ].map((area) => (
                  <a
                    key={area.name}
                    href={area.url}
                    className="px-4 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded-full text-sm border border-blue-500/30 hover:border-blue-500/50 transition-all"
                  >
                    {area.name} in Kollam
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-20 px-4 bg-gradient-to-b from-black to-slate-900">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-3 mb-4">
                <MapPin className="w-8 h-8 text-green-400" />
                <h2 className="text-3xl md:text-4xl font-bold">
                  Serving All of Kollam District
                </h2>
              </div>
              <p className="text-xl text-gray-400">
                Legal services available across Kollam | കൊല്ലം ജില്ലയിലെ എല്ലായിടത്തും സേവനം
              </p>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
              <div className="flex flex-wrap justify-center gap-3">
                {serviceAreas.map((area) => (
                  <span
                    key={area}
                    className="px-4 py-2 bg-green-500/10 text-green-400 rounded-full text-sm border border-green-500/30"
                  >
                    {area}
                  </span>
                ))}
              </div>
              <div className="mt-8 text-center">
                <p className="text-gray-300 mb-4">
                  <strong className="text-white">Office Location:</strong> 1st Floor, Ganga Building, 
                  Opposite Kollam Collectorate, Kollam - 691013
                </p>
                <p className="text-gray-400 text-sm">
                  Near Kollam District Court | Easy access from all parts of Kollam
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Client Testimonials */}
        <section className="py-20 px-4 bg-slate-900">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                What Our Kollam Clients Say
              </h2>
              <p className="text-xl text-gray-400">
                Real reviews from real clients across Kollam district
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div 
                  key={index}
                  className="p-6 bg-slate-800/50 rounded-xl border border-slate-700 hover:border-green-400/50 transition-all"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-300 mb-4 italic">"{testimonial.text}"</p>
                  <div className="pt-4 border-t border-slate-700">
                    <p className="font-semibold text-white">{testimonial.name}</p>
                    <p className="text-sm text-gray-400">{testimonial.location}</p>
                    <p className="text-xs text-green-400 mt-1">{testimonial.case}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <div className="inline-flex items-center gap-2 bg-green-500/10 px-6 py-3 rounded-full border border-green-500/30">
                <Star className="w-5 h-5 text-green-400 fill-green-400" />
                <span className="text-white font-semibold">4.8/5 Rating</span>
                <span className="text-gray-400">• 250+ Reviews</span>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-4 bg-gradient-to-b from-slate-900 to-black">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Frequently Asked Questions About Our Kollam Law Firm
            </h2>

            <div className="space-y-6">
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-white mb-3">
                  Why is LoopHoleLaw considered the best law firm in Kollam?
                </h3>
                <p className="text-gray-300">
                  LoopHoleLaw has 7+ years of experience serving Kollam with a proven track record of 
                  100+ successful cases and a 92% success rate in criminal defense. We have deep expertise 
                  in Kollam District Court procedures and Kerala High Court matters. Our multilingual team 
                  serves clients in English, Malayalam, and Hindi.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-white mb-3">
                  What areas in Kollam district do you serve?
                </h3>
                <p className="text-gray-300">
                  We provide legal services across all of Kollam district including Kollam City, Karunagappally, 
                  Sasthamcotta, Kottarakkara, Punalur, Chavara, Paravur, Anchal, Kundara, and surrounding areas. 
                  Our office is conveniently located opposite Kollam Collectorate.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-white mb-3">
                  What legal services does LoopHoleLaw provide in Kollam?
                </h3>
                <p className="text-gray-300">
                  We specialize in Criminal Law (including bail, trial defense, appeals), Civil Litigation 
                  (property disputes, contracts), Cyber Law (online fraud, IT Act violations), Family Law 
                  (divorce, custody, maintenance), Property Law (land disputes, documentation), Corporate Law, 
                  and Intellectual Property Law.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-white mb-3">
                  Do you offer emergency legal services in Kollam?
                </h3>
                <p className="text-gray-300">
                  Yes! We provide 24/7 emergency legal support for urgent matters like arrests, police custody, 
                  anticipatory bail, and other time-sensitive cases across Kollam district. Call +91 9539650421 
                  for immediate assistance.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-white mb-3">
                  How much do your legal services cost in Kollam?
                </h3>
                <p className="text-gray-300">
                  Our fees depend on the complexity and type of case. We offer competitive rates for Kollam 
                  and Kerala. We provide free initial consultation to discuss your case and provide transparent 
                  pricing. Contact us for a detailed fee structure tailored to your legal needs.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-gradient-to-r from-blue-500/10 to-green-500/10 border-y border-slate-700" aria-label="contact">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Get Expert Legal Representation in Kollam Today
            </h2>
            <p className="text-xl text-gray-300 mb-2">
              കൊല്ലത്തെ മികച്ച അഭിഭാഷകരിൽ നിന്ന് സൗജന്യ കൺസൾട്ടേഷൻ നേടുക
            </p>
            <p className="text-gray-300 mb-8">
              Whether you're facing criminal charges, civil disputes, cyber crime issues, family matters, 
              or property conflicts, our experienced attorneys in Kollam are here to protect your rights.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a 
                href="/contact" 
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-green-500 hover:bg-green-600 rounded-lg font-semibold text-lg transition-colors"
              >
                <Phone className="w-5 h-5" />
                Free Consultation - Call Now
              </a>
              <a 
                href="/practice-areas" 
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white hover:bg-white/10 rounded-lg font-semibold text-lg transition-colors"
              >
                <Scale className="w-5 h-5" />
                View Practice Areas
              </a>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-gray-300">
              <div className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-green-400" />
                <a href="tel:+919539650421" className="hover:text-green-400 transition-colors">
                  +91 9539650421
                </a>
              </div>
              <div className="hidden sm:block text-gray-600">•</div>
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-green-400" />
                <span>Opposite Kollam Collectorate</span>
              </div>
              <div className="hidden sm:block text-gray-600">•</div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-green-400" />
                <span>English, മലയാളം, हिन्दी</span>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="py-12 px-4 bg-slate-900">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-wrap justify-center items-center gap-8 text-center">
              <div className="flex flex-col items-center">
                <Shield className="w-12 h-12 text-green-400 mb-2" />
                <p className="text-sm text-gray-400">Registered with<br/>Kerala Bar Council</p>
              </div>
              <div className="flex flex-col items-center">
                <Award className="w-12 h-12 text-green-400 mb-2" />
                <p className="text-sm text-gray-400">7+ Years<br/>Legal Experience</p>
              </div>
              <div className="flex flex-col items-center">
                <Users className="w-12 h-12 text-green-400 mb-2" />
                <p className="text-sm text-gray-400">100+ Satisfied<br/>Clients in Kollam</p>
              </div>
              <div className="flex flex-col items-center">
                <Star className="w-12 h-12 text-green-400 mb-2" />
                <p className="text-sm text-gray-400">4.8/5 Rating<br/>250+ Reviews</p>
              </div>
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

export default LLWhyUsPage;